/**
 * ConsentService
 *
 * API client for electronic delivery consent (給与明細書等電子交付同意書).
 * - getConsentStatus: check if user has already submitted consent (for first-time login modal)
 * - submitConsent: save agree/disagree when user clicks 同意する or 同意しない
 */

import { API_BASE_URL } from '../config/apiConfig';

const CONSENT_API_URL = `${API_BASE_URL}/consent`;

export type EmpType = 'office' | 'construction';
export type ConsentStatus = 'agree' | 'disagree';

export interface ConsentStatusResponse {
  hasConsented: boolean;
  status?: string;
  consentDate?: string;
  consentTime?: string;
}

export interface ConsentSubmitRequest {
  empCode: string;
  name: string;
  empType: EmpType;
  status: ConsentStatus;
}

function getAuthToken(): string | null {
  return sessionStorage.getItem('authToken');
}

function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  const token = getAuthToken();
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Get consent status for the given employee. Used to decide if first-time consent modal should be shown.
 */
export async function getConsentStatus(empCode: string, empType: EmpType): Promise<ConsentStatusResponse> {
  const url = `${CONSENT_API_URL}/status?empCode=${encodeURIComponent(empCode)}&empType=${encodeURIComponent(empType)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
  });
  const json = await response.json();
  if (!response.ok || !json.success) {
    return { hasConsented: false };
  }
  return json.data || { hasConsented: false };
}

/**
 * Submit consent (agree or disagree). Call when user clicks 同意する or 同意しない.
 */
export async function submitConsent(request: ConsentSubmitRequest): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(CONSENT_API_URL, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(request),
  });
  const json = await response.json();
  return {
    success: response.ok && json.success === true,
    message: json.message,
  };
}

/** Admin: latest consent per employee (one row per emp_id). */
export interface ConsentAgreedRow {
  empId: string;
  name: string;
  status: ConsentStatus;
  consentDate: string;
  consentTime: string;
}

/**
 * Admin: list latest consent per employee. Optional search by name or ID.
 */
export async function getConsentAgreedList(search?: string): Promise<ConsentAgreedRow[]> {
  const params = new URLSearchParams();
  if (search != null && search.trim() !== '') params.set('search', search.trim());
  const q = params.toString();
  const url = q ? `${CONSENT_API_URL}/admin/list?${q}` : `${CONSENT_API_URL}/admin/list`;
  const response = await fetch(url, { method: 'GET', headers: getHeaders() });
  const json = await response.json();
  if (!response.ok || !json.success) return [];
  return json.data ?? [];
}

/**
 * Admin: name suggestions for autocomplete (names starting with prefix).
 */
export async function getConsentNameSuggestions(prefix: string): Promise<string[]> {
  const trimmed = prefix.trim();
  if (trimmed === '') return [];
  const url = `${CONSENT_API_URL}/admin/names?prefix=${encodeURIComponent(trimmed)}`;
  const response = await fetch(url, { method: 'GET', headers: getHeaders() });
  const json = await response.json();
  if (!response.ok || !json.success) return [];
  return json.data ?? [];
}

/**
 * Admin: download consent logs as CSV for the given date range.
 * fromDate, toDate in YYYY-MM-DD. Triggers browser download; throws on error.
 */
export async function downloadConsentCsv(fromDate: string, toDate: string): Promise<void> {
  const params = new URLSearchParams({
    fromDate,
    toDate,
  });
  const url = `${CONSENT_API_URL}/admin/export/csv?${params.toString()}`;
  const response = await fetch(url, { method: 'GET', headers: getHeaders() });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'CSVのダウンロードに失敗しました。');
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  let filename = `電子交付同意_${fromDate}_${toDate}.csv`;
  if (disposition) {
    const utf8Match = /filename\*=UTF-8''([^;\n]+)/i.exec(disposition);
    if (utf8Match?.[1]) {
      try {
        filename = decodeURIComponent(utf8Match[1].trim());
      } catch {
        /* keep fallback */
      }
    } else {
      const match = /filename[^;=\n]*=['"]?([^'"\n]+)['"]?/i.exec(disposition);
      if (match?.[1]) filename = match[1].trim();
    }
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

/** Global consent form template (GET /api/consent/form-config). */
export interface ConsentFormSectionPayload {
  title: string;
  lines: string[];
}

export interface ResolvedConsentFormConfigPayload {
  sections: ConsentFormSectionPayload[];
  printNoticeLines: string[];
}

/**
 * Resolved consent template for modal / PDF (any authenticated user).
 */
export async function getConsentFormConfig(): Promise<ResolvedConsentFormConfigPayload | null> {
  const url = `${CONSENT_API_URL}/form-config`;
  const response = await fetch(url, { method: 'GET', headers: getHeaders() });
  const json = await response.json();
  if (!response.ok || !json.success) return null;
  return json.data ?? null;
}

export interface ConsentFormConfigUpdatePayload {
  sections: ConsentFormSectionPayload[];
  printNoticeLines: [string, string];
}

/**
 * Admin: save global consent template.
 */
export async function putConsentFormConfig(
  body: ConsentFormConfigUpdatePayload,
): Promise<{ success: boolean; data?: ResolvedConsentFormConfigPayload; message?: string }> {
  const response = await fetch(`${CONSENT_API_URL}/admin/form-config`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({
      sections: body.sections,
      printNoticeLines: body.printNoticeLines,
    }),
  });
  const json = await response.json();
  return {
    success: response.ok && json.success === true,
    data: json.data,
    message: json.message,
  };
}

/**
 * Admin: reset template to application defaults (default body + footnote).
 */
export async function postConsentFormConfigReset(): Promise<{
  success: boolean;
  data?: ResolvedConsentFormConfigPayload;
  message?: string;
}> {
  const response = await fetch(`${CONSENT_API_URL}/admin/form-config/reset`, {
    method: 'POST',
    headers: getHeaders(),
  });
  const json = await response.json();
  return {
    success: response.ok && json.success === true,
    data: json.data,
    message: json.message,
  };
}
